import type { ICellRendererParams } from 'ag-grid-community';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ProjectPrompt } from '../hooks/useProjectData';
import { ProviderIcons } from '../components/prompts/ProviderIcons';
import { StatusBadge } from '../components/MetricsCards';
import { PromptTopBrands } from '../components/prompts/PromptTopBrands';
import { VisibilityBar } from '../components/visibility/VisibilityBadges';
import { formatUtcDate } from '@/helpers/formatUtcDate';
import { getHelpHtml } from '../utils/help/help-content';

function columnHelp(key: string): string | undefined {
  return getHelpHtml(key) || undefined;
}

export type PromptRow = ProjectPrompt;

export interface PromptsColumnCallbacks {
  selectedId: string | null;
  runningIdSet: Set<string>;
  showTopic: boolean;
  onSelect: (id: string) => void;
  onRun: (id: string) => void;
  onStop: () => void;
}

export function promptsColumnConfig(callbacks: PromptsColumnCallbacks) {
  const cols: Array<Record<string, unknown>> = [
    {
      field: 'text',
      title: 'پرامپت',
      tooltip: columnHelp('tables.prompt'),
      type: 'string',
      flex: 1,
      minWidth: 260,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptRow>) => {
        const row = params.data;
        if (!row) return null;
        const active = callbacks.selectedId === row.id;
        return (
          <button
            type="button"
            className={`btn btn-link p-0 text-start text-decoration-none w-100 fs--1 ${
              active ? 'fw-semibold text-primary' : 'text-body'
            }`}
            onClick={() => callbacks.onSelect(row.id)}
          >
            <span className="d-block text-truncate lh-sm">{row.text}</span>
            {callbacks.showTopic && row.topicName ? (
              <span className="d-block fs--2 text-primary fw-medium">{row.topicName}</span>
            ) : null}
          </button>
        );
      },
    },
    {
      field: 'brands',
      title: 'برندهای برتر',
      tooltip: columnHelp('tables.top_brands'),
      type: 'string',
      width: 156,
      minWidth: 140,
      maxWidth: 176,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptRow>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <PromptTopBrands status={row.status} runs={row.runs ?? 0} brands={row.brands} />
        );
      },
    },
    {
      field: 'providers',
      title: 'مدل‌ها',
      tooltip: columnHelp('tables.provider'),
      type: 'string',
      width: 100,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptRow>) =>
        params.data ? (
          <div className="w-100 h-100 d-flex align-items-center">
            <ProviderIcons providers={params.data.providers ?? []} />
          </div>
        ) : null,
    },
    {
      field: 'status',
      title: 'وضعیت',
      tooltip: columnHelp('tables.status'),
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptRow>) => {
        const row = params.data;
        if (!row) return null;
        const running = callbacks.runningIdSet.has(row.id);
        return <StatusBadge status={running ? 'running' : row.status} />;
      },
    },
    {
      field: 'domainVisibility',
      title: 'دیده‌شدن در AI',
      tooltip: columnHelp('metrics.ai_visibility'),
      type: 'number',
      width: 150,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptRow>) => {
        const score = params.data?.domainVisibility;
        if (score == null) return <span className="fs--2 text-300">—</span>;
        return <VisibilityBar score={score} />;
      },
    },
    {
      field: 'domainAvgPosition',
      title: 'میانگین رتبه',
      tooltip: columnHelp('tables.avg_position'),
      type: 'number',
      width: 110,
      searchable: true,
      valueFormatter: (p: { value: number | null | undefined }) =>
        p.value != null ? `#${p.value}` : '—',
    },
    {
      field: 'domainAiMentions',
      title: 'منشن در AI',
      tooltip: columnHelp('metrics.ai_mentions'),
      type: 'number',
      width: 110,
      searchable: true,
      valueFormatter: (p: { value: number | null | undefined }) =>
        p.value != null ? String(p.value) : '—',
    },
    {
      field: 'runs',
      title: 'اجراها',
      tooltip: columnHelp('tables.executions'),
      type: 'number',
      width: 90,
      searchable: true,
    },
    {
      field: 'lastRunAt',
      title: 'آخرین اجرا',
      tooltip: columnHelp('tables.last_run'),
      type: 'string',
      width: 140,
      searchable: true,
      valueFormatter: (p: { value: string | null | undefined }) =>
        formatUtcDate(p.value, { preset: 'dateTime' }),
    },
    {
      field: 'createdAt',
      title: 'ایجاد شده',
      type: 'string',
      width: 140,
      searchable: true,
      valueFormatter: (p: { value: string | null | undefined }) =>
        formatUtcDate(p.value, { preset: 'dateTime' }),
    },
    {
      field: '_actions',
      title: ' ',
      type: 'string',
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      flex: 0,
      resizable: false,
      sortable: false,
      searchable: false,
      noFilter: true,
      pinned: 'left',
      lockPosition: true,
      cellClass: 'over-flow',
      cellStyle: { textAlign: 'center', margin: '0px' },
      cellRenderer: (params: ICellRendererParams<PromptRow>) => {
        const row = params.data;
        if (!row) return null;
        const running = callbacks.runningIdSet.has(row.id);
        return (
          <div className="w-100 d-flex h-100 align-items-center justify-content-center">
            <span className="allaho_akbar_zIndex">
              <Dropdown
                onClick={(e) => e.stopPropagation()}
                align="start"
                className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
              >
                <Dropdown.Toggle
                  className="text-600 btn-reveal"
                  split
                  variant="reveal"
                  size="sm"
                >
                  <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="border py-0"
                  popperConfig={{ strategy: 'fixed' }}
                  renderOnMount
                >
                  <Dropdown.Item
                    onClick={() => callbacks.onSelect(row.id)}
                    className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                    مشاهده
                  </Dropdown.Item>
                  {running ? (
                    <Dropdown.Item
                      onClick={() => callbacks.onStop()}
                      className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
                    >
                      <FontAwesomeIcon icon="stop-circle" className="me-1 ms-2" />
                      توقف اجرا
                    </Dropdown.Item>
                  ) : (
                    <Dropdown.Item
                      onClick={() => callbacks.onRun(row.id)}
                      className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
                    >
                      <FontAwesomeIcon icon="play" className="me-1 ms-2" />
                      اجرا
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </span>
          </div>
        );
      },
    },
  ];

  return cols;
}
